/**
 * K-MRS 접근코드 원문 [복사] — reveal로 받은 원문을 클립보드에만 넣는다.
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — D7a, 화면 설계 A
 *
 * #125 연동 키 원문 복사(`RawKeyModals.copyRevealedKey`)와 같은 방식이다: ClipboardItem(text/plain =
 * reveal 결과 Blob)을 **동기적으로** 시작해 Safari의 사용자 제스처를 잃지 않게 한다. ClipboardItem이
 * 없거나(jsdom·구형) 실패하면 writeText로, 그것도 막히면 공용 원문 창(showSecretReveal)으로 대신
 * 보여준다(직접 복사). reveal 자체가 실패하면(409 원문 없음 등) 조용히 멈춘다 — 사유는 훅(onError)이
 * 토스트로 보여준다. (#125와 결과 모양이 `{rawKey}`↔`{code}`로 달라 그 함수를 공유하지 않고 복제한다.)
 */
import { ToastManager } from "components/organisms/CommonUI";
import { showSecretReveal } from "../SecretRevealModal";

export const CODE_COPY_DONE = "복사했어요";
/** 클립보드가 막혀 원문 창으로 대체할 때의 안내(발급 창·「보기」 문구와 다르다). */
export const CODE_COPY_ALT_NOTICE = "클립보드에 넣지 못해 원문을 보여 드립니다. 직접 복사하세요";

/** ClipboardItem 경로가 이 환경에서 쓸 수 있는가(Safari 등). jsdom·구형은 writeText로 내려간다. */
const canUseClipboardItem = (): boolean =>
  typeof ClipboardItem !== "undefined" && typeof navigator !== "undefined" && !!navigator.clipboard?.write;

/**
 * reveal로 원문을 받아 클립보드에 쓴다(순서는 #125 계약과 같다):
 *  1) ClipboardItem(text/plain = reveal 결과 Blob)을 **동기적으로** 시작한다.
 *  2) ClipboardItem이 없거나 실패하면 reveal 결과로 writeText.
 *  3) 그것도 실패하면 원문 표시 창(대체 경로, 안내 문구가 다르다).
 * reveal 실패(409 등)면 조용히 멈춘다 — 사유는 훅(onError)이 토스트로 보여준다.
 */
export const copyRevealedCode = async (
  reveal: () => Promise<{ code: string }>,
  opts: { onSettled?: () => void } = {}
): Promise<void> => {
  const revealPromise = reveal();
  // 1) ClipboardItem 경로를 동기적으로 시작한다(첫 await 전).
  let itemWrite: Promise<void> | null = null;
  if (canUseClipboardItem()) {
    try {
      const item = new ClipboardItem({
        "text/plain": revealPromise.then((r) => new Blob([r.code], { type: "text/plain" })),
      });
      itemWrite = navigator.clipboard.write([item]);
    } catch {
      itemWrite = null;
    }
  }

  try {
    if (itemWrite) {
      try {
        await itemWrite;
        await revealPromise; // 성공 확정(토스트 전 reveal 성공 보장)
        ToastManager.show({ content: CODE_COPY_DONE, duration: 2000 });
        return;
      } catch {
        // ClipboardItem 실패 — reveal 실패인지 클립보드 거부인지 아래에서 가린다.
      }
    }

    let code: string;
    try {
      ({ code } = await revealPromise);
    } catch {
      return; // 사유는 훅(onError) 토스트
    }
    try {
      await navigator.clipboard.writeText(code);
      ToastManager.show({ content: CODE_COPY_DONE, duration: 2000 });
    } catch {
      showSecretReveal({ title: "접근코드 원문", raw: code, provision: null, notice: CODE_COPY_ALT_NOTICE });
    }
  } finally {
    opts.onSettled?.();
  }
};
